import React from 'react';

import { ThreeDStockLifestylesListItemPreviewLifestyle } from './ThreeDStockLifestylesListItemPreview.types';
import { IconsEnum } from '../../../../../../../assets/icons/types';
import {
  OnLifestyleSimilarSearchAction,
  UpdateLifestyleCacheAction
} from '../../../../../../lifestyles/lifestylesTypes';
import { TeamNanoID } from '../../../../../../teams/teamsTypes';

import { ThreeDStockLifestylesListItemButtons } from '../ThreeDStockLifestylesListItemButtons';

import { Icon } from '../../../../../../../helpers/Icon';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import {
  ImageHelper,
  ImageItemImageVersions
} from '../../../../../../../helpers/ImageHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { IconWithTooltip } from '../../../../../../../helpers/IconWithTooltip';

import { Files } from '../../../../../../../utils/Files';

import { LifestylesPermissions } from '../../../../../../lifestyles/lifestylesConstants';

import { librariesKeys, words } from '../../../../../../../locales/keys';

const srcSetVersions = {
  '1x': ImageItemImageVersions.MiniThumb320x320,
  '2x': ImageItemImageVersions.MiniThumb640x640,
  '3x': ImageItemImageVersions.MiniThumb960x960
};

const realAspectRatioSrcSetVersions = {
  '1x': ImageItemImageVersions.BigThumb538x435,
  '2x': ImageItemImageVersions.BigThumb538x435,
  '3x': ImageItemImageVersions.FullScreenThumb1000x850
};

interface ThreeDStockLifestylesListItemPreviewProps {
  lifestyle: ThreeDStockLifestylesListItemPreviewLifestyle;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  onLightboxOpen: () => void;
  lifestyleEditPagePath: string;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesListItemPreviewLifestyle>;
  onEditButtonMouseEnter?: () => void;
  onLifestyleSimilarSearchFilter?: OnLifestyleSimilarSearchAction;
  withRealAspectRatio?: boolean;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
}

function ThreeDStockLifestylesListItemPreview({
  lifestyle,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  onLightboxOpen,
  updateLifestyleCache,
  onEditButtonMouseEnter,
  lifestyleEditPagePath,
  onLifestyleSimilarSearchFilter,
  withRealAspectRatio,
  companyNanoId,
  isMyLibrary
}: ThreeDStockLifestylesListItemPreviewProps) {
  return (
    <div className="aspect-w-1 aspect-h-1 w-full bg-gray-100 dark:bg-gray-800 order-first">
      {lifestyle.image?.file && Files.isImage(lifestyle.image?.file) ? (
        <ImageHelper
          className="object-contain bg-gray-50 dark:bg-gray-850 border-b dark:border-gray-700 w-full h-full"
          src={lifestyle.image.file}
          version={ImageItemImageVersions.MiniThumb320x320}
          srcSetVersions={
            withRealAspectRatio ? realAspectRatioSrcSetVersions : srcSetVersions
          }
          alt={lifestyle.name}
        />
      ) : (
        <div className="flex items-center justify-center text-gray-600 absolute inset-0">
          <Icon icon={IconsEnum.PHOTOGRAPH_OUTLINE} className="h-12 w-12" />
        </div>
      )}

      <div className="absolute inset-0 md:bg-black md:bg-opacity-0 pointer-events-none md:group-hover:bg-opacity-40 group-focus-within:bg-opacity-40 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-color duration-150">
        <div className="absolute top-0 left-0 pt-3 pl-3">
          <CheckPermissions
            action={LifestylesPermissions.READ_LIFESTYLE_PREMIUM_CONTENT_AT}
          >
            {lifestyle.premiumContentAt ? (
              <div className="p-1 rounded bg-gray-900/75 text-yellow-500">
                <IconWithTooltip
                  icon={IconsEnum.CROWN_SOLID}
                  iconClassName="h-5 w-5"
                  i18nText={librariesKeys.premium}
                />
              </div>
            ) : null}
          </CheckPermissions>
        </div>

        <div className="absolute bottom-0 left-0 w-full pb-3 px-3 md:opacity-0 pointer-events-none md:group-hover:opacity-100 group-hover:pointer-events-auto md:group-focus-within:opacity-100 transition-opacity duration-150 ease-out z-[2]">
          <ThreeDStockLifestylesListItemButtons
            lifestyle={lifestyle}
            downloadButtonAction={downloadButtonAction}
            downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
            updateLifestyleCache={updateLifestyleCache}
            onEditButtonMouseEnter={onEditButtonMouseEnter}
            lifestyleEditPagePath={lifestyleEditPagePath}
            onLifestyleSimilarSearchFilter={onLifestyleSimilarSearchFilter}
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />

          <div>
            <PureIconButtonHelper
              className="bg-white text-black hover:bg-gray-100 p-2 w-full justify-center flex gap-2 items-center rounded-full mt-2 text-sm"
              iconClassName="h-5 w-5 stroke-2"
              icon={IconsEnum.ARROWS_EXPAND_OUTLINE}
              i18nTextClassName="block"
              i18nText={words.expand_view}
              onClick={onLightboxOpen}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockLifestylesListItemPreview;
